import { useState, useEffect } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

import logo from '../../../assets/images/SVG/eduoneLogo.svg';
import AuthLeftPanel from '../../common/AuthLeftPanel';
import leftImage from '../../../assets/images/left-image-2.jpg';
import registerService from '../../../infrastructure/services/registerService';
import LoginOTPVerificationModal from '../Login/LoginOTPVerificationModal';
import { setCookie } from '../../../infrastructure/utils/cookieStorage';
import { validatePhoneNumber, validateFullName, validateDateOfBirth, getPhoneMaxLength } from '../../../infrastructure/utils/validation';

const getFieldSx = (hasError, disabled = false) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '16px',
        backgroundColor: disabled ? '#f8fafc' : '#ffffff',
        paddingLeft: '0px',
        opacity: disabled ? 0.7 : 1,
        '& fieldset': {
            borderWidth: '1.5px',
            borderColor: hasError ? '#e74c3c' : '#cbd5e1'
        },
        '&:hover fieldset': {
            borderColor: hasError ? '#e74c3c' : (disabled ? '#cbd5e1' : '#94a3b8')
        },
        '&.Mui-focused fieldset': {
            borderColor: '#ff502d',
            borderWidth: '1.5px'
        },
        '&.Mui-focused': {
            boxShadow: hasError ? '0 0 0 3px rgba(231, 76, 60, 0.1)' : '0 0 0 3px rgba(255, 80, 45, 0.1)'
        },
        '& input': {
            color: '#334155',
            fontFamily: "'Poppins', sans-serif",
            fontSize: '15px',
            padding: '16px 14px 16px 12px',
            cursor: disabled ? 'not-allowed' : 'pointer'
        }
    }
});

const autocompletePaperSx = {
    mt: 0.5,
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
    border: '1px solid #f1f5f9',
    overflow: 'hidden',
    '& .MuiAutocomplete-listbox': {
        p: '6px',
        maxHeight: '280px',
        '&::-webkit-scrollbar': {
            width: '6px',
        },
        '&::-webkit-scrollbar-track': {
            background: '#f1f5f9',
            borderRadius: '9999px',
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#cbd5e1',
            borderRadius: '9999px',
            '&:hover': {
                background: '#94a3b8',
            }
        },
        '& .MuiAutocomplete-option': {
            borderRadius: '10px',
            fontFamily: "'Poppins', sans-serif",
            fontSize: '13.5px',
            color: '#1e293b',
            fontWeight: 500,
            px: 2,
            py: '10px',
            mb: '2px',
            '&:hover': { bgcolor: '#fff4f2' },
            '&[aria-selected="true"]': {
                bgcolor: '#fff4f2',
                color: '#ff502d',
                fontWeight: 600
            },
            '&[aria-selected="true"]:hover': { bgcolor: '#ffe8e3' }
        }
    }
};

const GENDER_OPTIONS = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' }
];

const RegisterPage = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Determine the active step based on current nested route path
    let step = 1;
    if (location.pathname.endsWith('/step-2')) {
        step = 2;
    } else if (location.pathname.endsWith('/step-3')) {
        step = 3;
    }

    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        countryCode: '+94',
        dateOfBirth: '',
        gender: '',
        city: '',
        district: '',
        password: '',
        confirmPassword: '',
        agreeToTerms: false
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
    const [showOTPVerification, setShowOTPVerification] = useState(false);
    const [verificationData, setVerificationData] = useState(null);

    // Prevent back navigation when registration OTP verification is active
    useEffect(() => {
        if (!showOTPVerification) return;

        window.history.pushState(null, document.title, window.location.href);

        const handlePopState = (e) => {
            window.history.pushState(null, document.title, window.location.href);
            setToast({
                open: true,
                message: 'Please complete the OTP verification to finalize your registration, or close the modal to cancel.',
                severity: 'warning'
            });
        };

        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [showOTPVerification]);

    const handleOTPSuccess = async (result) => {
        setShowOTPVerification(false);
        setToast({ open: true, message: 'OTP verified successfully! Logging you in...', severity: 'success' });

        try {
            const { accessToken, user } = result;

            setCookie('authToken', accessToken);
            setCookie('student_auth_token', accessToken);
            setCookie('student_user', user);
            setCookie('studentId', verificationData?.studentId || '');

            setToast({ open: true, message: 'Logged in successfully!', severity: 'success' });

            setTimeout(() => {
                navigate('/dashboard');
            }, 600);
        } catch (err) {
            console.error('Auto-login failed:', err);
            setToast({ open: true, message: 'Verification succeeded but auto-login failed. Please log in manually.', severity: 'warning' });
            setTimeout(() => {
                navigate('/');
            }, 2500);
        }
    };


    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        let newValue = type === 'checkbox' ? checked : value;

        if (name === 'fullName' && typeof newValue === 'string') {
            // Trim leading spaces immediately
            let val = newValue.replace(/^\s+/, '');
            // Restrict multiple consecutive spaces
            val = val.replace(/\s{2,}/g, ' ');
            // Enforce maximum character limit of 50
            if (val.length > 50) {
                val = val.substring(0, 50);
            }
            newValue = val;
        }

        if (name === 'phone' && typeof newValue === 'string') {
            // Keep only digits
            newValue = newValue.replace(/\D/g, '');
            // Automatically strip leading 0
            if (newValue.startsWith('0')) {
                newValue = newValue.substring(1);
            }
            // Limit character length based on country code
            const maxLen = getPhoneMaxLength(formData.countryCode || '+94');
            if (newValue.length > maxLen) {
                newValue = newValue.substring(0, maxLen);
            }
        }

        setFormData(prev => {
            const updated = {
                ...prev,
                [name]: newValue
            };
            if (name === 'district') {
                updated.city = '';
            }
            return updated;
        });

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: '',
                ...(name === 'district' ? { city: '' } : {})
            }));
        }
    };

    const handleNextStep = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        const newErrors = {};

        if (step === 1) {
            const fullNameRaw = formData.fullName || '';
            const dateOfBirth = formData.dateOfBirth;
            const gender = formData.gender;

            const fullNameError = validateFullName(fullNameRaw);
            if (fullNameError) {
                newErrors.fullName = fullNameError;
            }

            const dobError = validateDateOfBirth(dateOfBirth);
            if (dobError) {
                newErrors.dateOfBirth = dobError;
            }
            if (!gender) {
                newErrors.gender = 'Gender is required';
            }

            if (Object.keys(newErrors).length > 0) {
                setErrors(newErrors);
                setToast({ open: true, message: 'Please correct the errors in Step 1 before proceeding', severity: 'error' });
                return;
            }

            // Save trimmed fullName back into form state
            setFormData(prev => ({
                ...prev,
                fullName: fullNameRaw.trim()
            }));

            setErrors({});
            navigate('/register/step-2');
        } else if (step === 2) {
            const email = (formData.email || '').trim();
            const district = (formData.district || '').trim();
            const city = (formData.city || '').trim();

            if (email && !emailRegex.test(email)) {
                newErrors.email = 'Please enter a valid email address';
            }

            const phoneError = validatePhoneNumber(formData.countryCode || '+94', formData.phone);
            if (phoneError) {
                newErrors.phone = phoneError;
            }

            if (!district) newErrors.district = 'Province is required';
            if (!city) newErrors.city = 'District is required';

            if (Object.keys(newErrors).length > 0) {
                setErrors(newErrors);
                setToast({ open: true, message: 'Please correct the errors in Step 2 before proceeding', severity: 'error' });
                return;
            }

            setErrors({});
            navigate('/register/step-3');
        }
    };

    const handleRegister = async (e) => {
        e?.preventDefault();
        const fullNameRaw = formData.fullName || '';
        const fullName = fullNameRaw.trim();
        const email = (formData.email || '').trim();
        const phone = (formData.phone || '').trim();
        const dateOfBirth = formData.dateOfBirth;
        const gender = formData.gender;
        const province = (formData.district || '').trim();
        const district = (formData.city || '').trim();
        const password = (formData.password || '').trim();
        const confirmPassword = (formData.confirmPassword || '').trim();
        const agreeToTerms = formData.agreeToTerms;

        const newErrors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        // Step 1 validation
        const fullNameError = validateFullName(fullNameRaw);
        if (fullNameError) {
            newErrors.fullName = fullNameError;
        }
        const dobError = validateDateOfBirth(dateOfBirth);
        if (dobError) {
            newErrors.dateOfBirth = dobError;
        }
        if (!gender) {
            newErrors.gender = 'Gender is required';
        }

        // Step 2 validation
        if (email && !emailRegex.test(email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        const phoneError = validatePhoneNumber(formData.countryCode || '+94', formData.phone);
        if (phoneError) {
            newErrors.phone = phoneError;
        }
        if (!province) newErrors.district = 'Province is required';
        if (!district) newErrors.city = 'District is required';

        // Step 3 validation
        const passwordRules = {
            length: password.length >= 6
        };

        if (!password) {
            newErrors.password = 'Password is required';
        } else if (!passwordRules.length) {
            newErrors.password = 'Password must be at least 6 characters long';
        }
        if (password !== confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }
        if (!agreeToTerms) {
            newErrors.agreeToTerms = 'You must agree to the Terms & Conditions';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            if (newErrors.fullName || newErrors.dateOfBirth || newErrors.gender) {
                navigate('/register/step-1');
                setToast({ open: true, message: 'Please correct the errors in Step 1 first', severity: 'error' });
            } else if (newErrors.email || newErrors.phone || newErrors.district || newErrors.city) {
                navigate('/register/step-2');
                setToast({ open: true, message: 'Please correct the errors in Step 2 first', severity: 'error' });
            } else {
                setToast({ open: true, message: 'Please correct the errors in Step 3 before submitting', severity: 'error' });
            }
            return;
        }

        // Save trimmed fullName back into form state
        setFormData(prev => ({
            ...prev,
            fullName: fullName
        }));

        setLoading(true);

        try {
            const content = await registerService.register({
                fullName,
                email,
                phoneNumber: (formData.countryCode || '+94') + phone,
                password,
                nic: '',
                dateOfBirth,
                gender,
                agreeToTerms,
                province,
                district
            });

            if (content?.student_id) {
                setCookie('studentId', content.student_id);
            }

            setToast({ open: true, message: 'User registered successfully. An OTP has been sent for verification.', severity: 'success' });

            setVerificationData({
                userId: content?.user?.id,
                phoneNumber: content?.user?.phone_number || ((formData.countryCode || '+94') + phone),
                email: content?.user?.email || email,
                studentId: content?.student_id || '',
                password: password
            });
            setShowOTPVerification(true);
        } catch (err) {
            console.error(err);
            const errMsg = err.message || '';
            const lowerMsg = errMsg.toLowerCase();

            if (lowerMsg.includes('email')) {
                navigate('/register/step-2');
                setErrors(prev => ({
                    ...prev,
                    email: errMsg.replace('Registration failed: ', '').trim()
                }));
            } else if (lowerMsg.includes('phone') || lowerMsg.includes('mobile')) {
                navigate('/register/step-2');
                setErrors(prev => ({
                    ...prev,
                    phone: errMsg.replace('Registration failed: ', '').trim()
                }));
            }

            setToast({ open: true, message: errMsg || 'Registration failed. Please try again.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                height: { xs: 'auto', md: '100vh' },
                minHeight: '100vh',
                alignItems: 'stretch',
                padding: { xs: '16px', md: '20px', lg: '24px' },
                gap: { xs: '16px', md: '20px', lg: '24px' },
                background: '#F5F7F9',
                boxSizing: 'border-box',
                overflow: { xs: 'auto', md: 'hidden' }
            }}
        >
            <Snackbar
                open={toast.open}
                autoHideDuration={3000}
                onClose={() => setToast((t) => ({ ...t, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{
                    '& .MuiPaper-root': {
                        borderRadius: 12,
                        boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
                    }
                }}
            >
                <Alert
                    onClose={() => setToast((t) => ({ ...t, open: false }))}
                    severity={toast.severity}
                    variant="filled"
                    sx={{
                        width: '100%',
                        borderRadius: '12px'
                    }}
                >
                    {toast.message}
                </Alert>
            </Snackbar>

            <AuthLeftPanel image={leftImage} />

            <Box
                sx={{
                    flex: 1.1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: { xs: '20px', md: '24px 0 12px 0' },
                    boxSizing: 'border-box',
                    height: { xs: 'auto', md: '100%' },
                    overflowY: { xs: 'visible', md: 'auto' },
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': {
                        display: 'none'
                    }
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: '480px',
                        my: 'auto'
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            mb: { xs: 2, md: 2.5 }
                        }}
                    >
                        <Box
                            component="img"
                            src={logo}
                            alt="EduOne"
                            sx={{
                                height: '45px',
                                width: 'auto'
                            }}
                        />
                    </Box>

                    <Box sx={{ textAlign: 'center', mb: { xs: 2, md: 2.5 } }}>
                        <Typography
                            variant="h1"
                            sx={{
                                fontSize: { xs: '26px', sm: '28px' },
                                fontWeight: 700,
                                color: '#231F20',
                                m: 0,
                                fontFamily: "'Poppins', sans-serif"
                            }}
                        >
                            Create Student Account
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: '14px',
                                color: '#9B9B9B',
                                lineHeight: 1.5,
                                mt: 1,
                                fontFamily: "'Poppins', sans-serif"
                            }}
                        >
                            Register to access your courses, live classes, and manage your academic profile.
                        </Typography>
                    </Box>

                    {/* Visual Stepper */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1.5,
                            mb: { xs: 2.5, md: 3 },
                            px: 1
                        }}
                    >
                        {/* Step 1 Label/Icon */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                                sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 600,
                                    fontSize: '13px',
                                    fontFamily: "'Poppins', sans-serif",
                                    bgcolor: step >= 1 ? '#ff502d' : '#e2e8f0',
                                    color: step >= 1 ? '#ffffff' : '#64748b',
                                    boxShadow: step >= 1 ? '0 4px 10px rgba(255, 80, 45, 0.25)' : 'none',
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                1
                            </Box>
                            <Typography
                                sx={{
                                    fontSize: '13px',
                                    fontWeight: step === 1 ? 600 : 500,
                                    color: step === 1 ? '#231F20' : '#94a3b8',
                                    fontFamily: "'Poppins', sans-serif"
                                }}
                            >
                                Personal Info
                            </Typography>
                        </Box>

                        {/* Connector Line 1 */}
                        <Box
                            sx={{
                                flex: 0.3,
                                height: '2px',
                                bgcolor: step >= 2 ? '#ff502d' : '#cbd5e1',
                                transition: 'all 0.3s ease'
                            }}
                        />

                        {/* Step 2 Label/Icon */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                                sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 600,
                                    fontSize: '13px',
                                    fontFamily: "'Poppins', sans-serif",
                                    bgcolor: step >= 2 ? '#ff502d' : '#e2e8f0',
                                    color: step >= 2 ? '#ffffff' : '#64748b',
                                    boxShadow: step >= 2 ? '0 4px 10px rgba(255, 80, 45, 0.25)' : 'none',
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                2
                            </Box>
                            <Typography
                                sx={{
                                    fontSize: '13px',
                                    fontWeight: step === 2 ? 600 : 500,
                                    color: step === 2 ? '#231F20' : '#94a3b8',
                                    fontFamily: "'Poppins', sans-serif"
                                }}
                            >
                                Contact Info
                            </Typography>
                        </Box>

                        {/* Connector Line 2 */}
                        <Box
                            sx={{
                                flex: 0.3,
                                height: '2px',
                                bgcolor: step >= 3 ? '#ff502d' : '#cbd5e1',
                                transition: 'all 0.3s ease'
                            }}
                        />

                        {/* Step 3 Label/Icon */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                                sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 600,
                                    fontSize: '13px',
                                    fontFamily: "'Poppins', sans-serif",
                                    bgcolor: step >= 3 ? '#ff502d' : '#e2e8f0',
                                    color: step >= 3 ? '#ffffff' : '#64748b',
                                    boxShadow: step >= 3 ? '0 4px 10px rgba(255, 80, 45, 0.25)' : 'none',
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                3
                            </Box>
                            <Typography
                                sx={{
                                    fontSize: '13px',
                                    fontWeight: step === 3 ? 600 : 500,
                                    color: step === 3 ? '#231F20' : '#94a3b8',
                                    fontFamily: "'Poppins', sans-serif"
                                }}
                            >
                                Password
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        component="form"
                        onSubmit={handleRegister}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: { xs: 2, md: 2.2, lg: 2.5 }
                        }}
                    >
                        <Outlet
                            context={{
                                formData,
                                handleInputChange,
                                errors,
                                handleNextStep,
                                loading,
                                getFieldSx,
                                autocompletePaperSx,
                                GENDER_OPTIONS,
                                setFormData,
                                setErrors
                            }}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3, gap: 1 }}>
                        <Typography sx={{ color: '#475569', fontSize: '15px', fontWeight: 400, m: 0, fontFamily: "'Poppins', sans-serif" }}>
                            Already have an account ?
                        </Typography>
                        <Button
                            onClick={() => navigate('/')}
                            sx={{
                                background: 'none',
                                border: 'none',
                                color: '#ff502d',
                                fontSize: '16px',
                                fontWeight: 600,
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                p: 0,
                                minWidth: 0,
                                textTransform: 'none',
                                fontFamily: "'Poppins', sans-serif",
                                '&:hover': {
                                    color: '#e04324',
                                    textDecoration: 'underline',
                                    background: 'none'
                                }
                            }}
                        >
                            Login
                        </Button>
                    </Box>
                </Box>

                <Box sx={{ textAlign: 'center', mt: { xs: 2, md: 2.5 } }}>
                    <Typography sx={{ fontSize: '12px', color: '#cbd5e1', m: 0, fontFamily: "'Poppins', sans-serif" }}>
                        EduOne.lk © {new Date().getFullYear()}. All rights reserved.
                    </Typography>
                </Box>
            </Box>

            <LoginOTPVerificationModal
                open={showOTPVerification}
                onClose={() => {
                    setShowOTPVerification(false);
                    navigate('/', { replace: true });
                }}
                verificationData={verificationData}
                onSuccess={handleOTPSuccess}
            />
        </Box>
    );
};

export default RegisterPage;
